import axios from "axios";
import { DateTime } from "luxon";
import { getAccessToken } from "./util/authUtil.js";

const mainSiteBaseUrl = process.env.REACT_APP_MAIN_STORE_SITE_URL;

// New implementation for the 7 internal APIs only: separate key and header. Existing APIs unchanged.
const internalApiKey = (process.env.REACT_APP_INTERNAL_API_KEY || '').trim();
if (!internalApiKey) {
  console.warn('REACT_APP_INTERNAL_API_KEY is not set; /internal/** requests may fail with 401.');
}

const INTERNAL_API_KEY_HEADER = 'X-Internal-Api-Key';
// Many backends (e.g. Spring) expect this exact name; send both so either works.
const X_API_KEY_HEADER = 'x-api-key';

const INTERNAL_PATHS = {
  SUPPORT_CONTACT_US: '/internal/support/contact-us',
  PUBLIC_DOCUMENT: '/internal/public/document',
  CACHE: '/internal/cache',
  QUIZ_V1: '/internal/v1/quiz',
  FULFILL_PARTNER_RETAIL: '/internal/fulfill/v1/partnerRetailOrderFulfill',
  FULFILL_PARTNER_REFERRAL: '/internal/fulfill/v1/partnerReferral',
  BOOKING_V1: '/internal/booking/v1',
  /** @deprecated Set REACT_APP_BOOKING_API_PATH=/secure/booking/v1 to use legacy path. */
  BOOKING_V1_LEGACY: '/secure/booking/v1',
};

// Booking base path: use env override for legacy; default is /internal/booking/v1
const BOOKING_BASE_PATH = (process.env.REACT_APP_BOOKING_API_PATH || '').trim() || INTERNAL_PATHS.BOOKING_V1;

// Only /internal/* paths get X-Internal-Api-Key; legacy /secure/booking/v1 uses JWT only
const INTERNAL_PATH_PREFIXES = [
  INTERNAL_PATHS.SUPPORT_CONTACT_US,
  INTERNAL_PATHS.PUBLIC_DOCUMENT,
  INTERNAL_PATHS.CACHE,
  INTERNAL_PATHS.QUIZ_V1,
  INTERNAL_PATHS.FULFILL_PARTNER_RETAIL,
  INTERNAL_PATHS.FULFILL_PARTNER_REFERRAL,
  INTERNAL_PATHS.BOOKING_V1,
];

function isInternalPath(urlOrPath) {
  const pathOnly = (urlOrPath || '').split('?')[0];
  return INTERNAL_PATH_PREFIXES.some((prefix) => pathOnly.startsWith(prefix));
}

// Raw key for the 7 internal APIs only. Uses REACT_APP_INTERNAL_API_KEY (e.g. test_key). Backend: SECRET_INTERNAL_API_KEY.
function getInternalApiKey() {
  return internalApiKey;
}

/** Headers to add for /internal/* paths (X-Internal-Api-Key + x-api-key). Backend must allow these in CORS Access-Control-Allow-Headers. */
function getInternalApiKeyHeaders() {
  const key = getInternalApiKey();
  return {
    [INTERNAL_API_KEY_HEADER]: key,
    [X_API_KEY_HEADER]: key,
  };
}

export class InternalApiService {
  /* -------booking API---------
    ----------------------------*/
  async findBookings(body) {
    return this.__doBookingPOST("/findBookings", body);
  }

  async getAllBookingsForDateRange(fromDate, toDate) {
    const req = {
      criteria: "DATE_RANGE",
      fromDate: fromDate,
      toDate: toDate,
    };
    return this.findBookings(req);
  }

  async getAllBookingsForLastname(lastName, dob) {
    const req = {
      criteria: "LASTNAME_DOB",
      lastName: lastName,
      dob: dob,
    };
    return this.findBookings(req);
  }

  async getAllBookingsById(id) {
    const req = {
      criteria: "BOOKING_ID",
      bookingId: id,
    };
    return this.findBookings(req);
  }

  async getAllBookingsByMobile(mobile) {
    const req = {
      criteria: "MOBILE_NO",
      mobileNo: mobile,
    };
    return this.findBookings(req);
  }

  async getAllBookingsByEmail(email) {
    const req = {
      criteria: "EMAIL_ID",
      emailId: email,
    };
    return this.findBookings(req);
  }

  async getBookingDetails(id) {
    return this.__doBookingGET(`get/${id}`);
  }

  async getPresignedUrl(url) {
    return this.__getRequest(`/secure/file/presignedUrl?url=${url}`, "GET");
  }

  async getBookingOrderSummary(id) {
    return axios({
      method: "GET",
      url: `${getMainSiteBaseUrl()}/api/booking/orderPaySummary?id=${id}`,
    });
  }

  // create checkout
  async createCheckoutClinician(data) {
    return axios.post(`${getMainSiteBaseUrl()}/api/booking/clinicianCreateCheckout`, data);
  }

  async payBookingWithCheckout(data) {
    return axios.post(`${getMainSiteBaseUrl()}/api/booking/payBookingWithCheckout`, data);
  }

  // update patient info in booking
  async updatePatientInfo(bookingId, data) {
    const payload = {
      ...data,
      created: DateTime.utc().toISO(),
    };
    return this.__postRequest(bookingBaseUrlFor(`/updatePatientInfo/${bookingId}`), "PATCH", payload);
  }

  async updatePhotonERxPatientId(userId, photonERxPatientId) {
    return this.__postRequest(`/secure/patient/updatePhotonERxPatientId`, "POST", { userId, photonERxPatientId });
  }

  async createPatientOnPhoton(data) {
    const payload = {
      ...data,
    };
    return axios.post(`${mainSiteBaseUrl}/api/photon/createPatient`, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  }
  async getPatientPrescriptions(patientId) {
    const payload = {
      patientId: patientId,
    };
    return axios.post(`${mainSiteBaseUrl}/api/photon/getPatientPrescriptions`, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  //@ Update Patient Account and Profile details
  async updatePatientAccountDetails(data) {
    return this.__postRequest(`/secure/clinician/patientDetails`, "PUT", data);
  }

  async updatePharmacyPartner(pharmacyPartner, userId) {
    const encodedPartner = encodeURIComponent(pharmacyPartner);
    const encodedUserId = encodeURIComponent(userId);
    const payload = { pharmacyPartner, userId };
    return this.__postRequest(`/secure/clinician/pharmacy-partner/${encodedPartner}/${encodedUserId}`, "POST", payload);
  }

  async getPatientOrders(email) {
    return this.__getRequest(`/secure/clinician/order/${email}`, "GET");
  }
  async getClinicianOrderSummary(email) {
    return this.__getRequest(`/secure/clinician/orders/summary/${email}`, "GET");
  }

  // submit notes
  async submitNotes(bookingId, data) {
    return this.__postRequest(bookingBaseUrlFor(`/addNotes/${bookingId}`), "PATCH", data);
  }

  //$ Charting Notes API
  async createChartingNotes(data) {
    return this.__postRequest(`/secure/chartNote`, "POST", data);
  }
  async getChartingNotesWithPatientId(patientId) {
    return this.__getRequest(`/secure/chartNote/patient/${patientId}`, "GET");
  }
  async updateChartingNotes(id, data) {
    return this.__postRequest(`/secure/chartNote/${id}`, "PUT", data);
  }

  async uploadLabcorpResults(file, selectedOrderId, result) {
    return this.__postRequest(`/secure/clinician/labcorp/result/${selectedOrderId}/${result}`, "POST", file);
  }

  async getLatestChartNoteWithStatusDraft(refrenceId, refrenceType, personId) {
    // If refrenceId and refrenceType is not passed then it will return latest draft for personId
    if (!refrenceId || !refrenceType) return this.__getRequest(`/secure/chartNote/latestDraft?personId=${personId}`, "GET");
    // If refrenceId and refrenceType is passed then it will return latest draft for refrenceId and refrenceType
    return this.__getRequest(`/secure/chartNote/latestDraft?referenceId=${refrenceId}&referenceType=${refrenceType}&personId=${personId}`, "GET");
  }

  async getChartNotesForBooking(bookingId) { 
    return this.__getRequest(`/secure/chartNote/booking/${bookingId}`, "GET");
  }

  async getChartNotesForKit(kitId) { 
    return this.__getRequest(`/secure/chartNote/kit/${kitId}`, "GET");
  }

  async getChartNotesForTestResult(kitId) { 
    return this.__getRequest(`/secure/chartNote/testResult/${kitId}`, "GET");
  }

  // submit clinical notes
  async submitClinicalNotes(bookingId, data) {
    return this.__postRequest(bookingBaseUrlFor(`/addClinicalNotes/${bookingId}`), "PATCH", data);
  }

  async verifySelfCheckInIdentity(data) {
    return this.__postRequest(bookingBaseUrlFor(`/selfCheckIn/verifyIdentity`), "PATCH", data);
  }

  // cancel appointment
  async cancelAppointment(bookingId, data) {
    return this.__postRequest(bookingBaseUrlFor(`/cancel/${bookingId}`), "DELETE", data);
  }

  // complete Clinical appointment
  async completeClinicalServiceAppointment(bookingId, data) {
    return this.__doBookingPOST(`/completeClinical/${bookingId}`, data);
  }

  // complete appointment
  async completeAppointment(bookingId, data) {
    return this.__doBookingPOST(`/complete/${bookingId}`, data);
  }



  // mark Pre No Show Cancel
  // async markPreNoShowCancel(bookingId, comments, isNoShow = false){
  //   return this.__postRequest(bookingBaseUrlFor(`/markPreNoShowCancel/${bookingId}`), "POST", { bookingId, comments, isNoShow });
  // }

  // mark Confirmed
  async markConfirmed(bookingId, reason) {
    return this.__postRequest(bookingBaseUrlFor(`/markConfirmed/${bookingId}`), "PATCH", { "reason": reason });
  }

  // update a pre-existing product in booking
  async updateProductInBooking(bookingId, data) {
    return this.__postRequest(bookingBaseUrlFor(`/updateProduct/${bookingId}`), "PATCH", data);
  }

  // delete a pre-existing product from booking
  async deleteProductInBooking(bookingId, productId) {
    return this.__postRequest(bookingBaseUrlFor(`/deleteProduct/${bookingId}`), "POST", { uniqueId: productId });
  }

  // add new product
  async addNewProductToBooking(bookingId, data) {
    return this.__postRequest(bookingBaseUrlFor(`/addProduct/${bookingId}`), "POST", data);
  }
  // create checkout clinician

  // update booking status
  async updateBookingStatus(bookingId, data) {
    return this.__postRequest(bookingBaseUrlFor(`/updateStatus/${bookingId}`), "PATCH", data);
  }

  // update booking status
  async addTabOrLabelToBooking(bookingId, data) {
    return this.__postRequest(bookingBaseUrlFor(`/addTagOrLabel/${bookingId}`), "PATCH", data);
  }

  // assign to me
  async assignToMe(bookingId) {
    return this.__postRequest(bookingBaseUrlFor(`/assignToMe/${bookingId}`), "PATCH", { bookingId: bookingId });
  }

  // mark booking as no show or cancel
  async markPreNoShowCancel(bookingId, comments, isNoShow) {
    return this.__postRequest(bookingBaseUrlFor(`/markPreNoShowCancel`), "POST", { bookingId: bookingId, isNoShow, comments: comments });
  }

  // onboard status
  async onboardStatus(bookingId) {
    return this.__doBookingGET(`/onboardStatus/${bookingId}`);
  }
  // get all booking for patient using email
  async getAllPatientBookings(email) {
    if (!email || typeof email !== 'string') {
      throw new Error('Email parameter is required and must be a string');
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      throw new Error('Email cannot be empty');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      throw new Error('Invalid email format');
    }
    if (/\.\.|[\\/]/.test(trimmedEmail)) {
      throw new Error('Invalid email format');
    }
    return await this.__doBookingGET(`/patient/bookings/${trimmedEmail}`);
  }

  // send Summary Report
  async sendPatientSummary(bookingId, data) {
    return this.__doBookingPOST(`/sendPatientSummary/${bookingId}`, data);
  }

  async sendSignUpLinkToCustomer(bookingId) {
    return this.__doBookingPOST(`/sendSignUpLink/${bookingId}`, { bookingId: bookingId });
  }

  async sendPayLinkToCustomer(bookingId) {
    return this.__doBookingPOST(`/sendPayLink/${bookingId}`, { bookingId: bookingId });
  }

  async orderKitForBooking(bookingId, data) {
    return this.__doBookingPOST(`/orderKit/${bookingId}`, data);
  }

  async getKitsOfPatientByPatientId(patientId) {
    return this.__getRequest(`/secure/v2/kit/patient/${patientId}`, "GET");
  }
  async getAllOrdersByPatientEmail(patientEmail) {
    return this.__getRequest(`/secure/clinician/orders/summary/${patientEmail}`, "GET");
  }
  async getTestResultByKitId(kitId) {
    return this.__getRequest(`/secure/testResult/${kitId}`, "GET");
  }
  async getKitDetialsByKitId(kitId) {
    return this.__getRequest(`/secure/kit/${kitId}`, "GET");
  }

  async getEventsForPatient(email) {
    return this.__getRequest(`/secure/event/history/${email}`, "GET");
  }

  async getPatientInbox(patientId) {
    return this.__getRequest(`/secure/message/inbox/${patientId}`, "GET");
  }

  async uploadStiResults(data) {
    return this.__postRequest(`/secure/clinician/labcorp/result-sti`, "POST", data);
  }

  async uploadLabcorpResultsV1(data) {
    return this.__postRequest(`/secure/clinician/v1/labcorp/results`, "POST", data);
  }

  /* ---------------------------------
  ---------booking API Ends-----------*/

  async getClinicianProfile() {
    return this.__getRequest(`/secure/clinician/profile`, "GET");
  }

  async addClinicianProfileOnSignup(data) {
    return this.__postRequest("/secure/clinician/profile/create", "POST", data);
  }

  async createPrescription(data) {
    return this.__postRequest("/secure/clinician/createPrescription", "POST", data);
  }

  async updateClinicianProfile(data) {
    return this.__postRequest("/secure/clinician/profile/update", "PATCH", data);
  }

  async updateClinicianCalendar(data) {
    return this.__postRequest(`/secure/clinician/profile/updateCalendar`, "PATCH", data);
  }

  async addClinicianLicence(data) {
    return this.__postRequest(`/secure/clinician/profile/addLicence`, "PATCH", data);
  }

  async updateClinicianLicence(data) {
    return this.__postRequest(`/secure/clinician/profile/updateLicence`, "PATCH", data);
  }

  async deleteClinicianLicence(data) {
    return this.__postRequest(`/secure/clinician/profile/deleteLicence`, "DELETE", data);
  }

  async addClinicianTemplate(data) {
    return this.__postRequest(`/secure/clinician/template`, "POST", data);
  }
  async updateClinicianTemplate(data) {
    return this.__postRequest(`/secure/clinician/template`, "PUT", data);
  }
  async getClinicianTemplates() {
    return this.__getRequest(`/secure/clinician/template`, "GET");
  }

  async getAssignedLabRequests() {
    return await this.__getRequest("/secure/labRequest?queryType=ASSIGNED", "GET");
  }

  async getAssignedLabRequestsAll() {
    return await this.__getRequest("/secure/labRequest?queryType=ALL", "GET");
  }

  async getAssignedLabResults() {
    return await this.__getRequest("/secure/testResult?queryType=ASSIGNED", "GET");
  }

  async getAssignedLabResultsAll() {
    return await this.__getRequest("/secure/testResult?queryType=ALL", "GET");
  }

  async getAppointmentCount() {
    return await this.__doBookingGET("/summaryForClinician", "GET");
  }

  async getUserProfilePicById(userId) {
    return await this.__getRequest("/secure/user/patientProfilePic/" + userId, "GET");
  }

  async getLicense() {
    return await this.__getRequest("/secure/license", "GET");
  }

  async getAllLicenses() {
    return await this.__getRequest("/secure/license/all", "GET");
  }

  async getValidLicenses() {
    return await this.__getRequest("/secure/license/valid", "GET");
  }

  async getAllLicenseStates() {
    return await this.__getRequest("/secure/license/allStates", "GET");
  }

  async getValidLicenseStates() {
    return await this.__getRequest("/secure/license/validStates", "GET");
  }

  async getInValidLicenseStates() {
    return await this.__getRequest("/secure/license/inValidStates", "GET");
  }

  async getValidAndInvalidLicenseStates() {
    return await this.__getRequest("/secure/license/validAndInvalidStates", "GET");
  }

  // async getLicense() {
  //   return await this.__getRequest('/secure/license', 'GET')
  // }

  async getMessage(messageUUID) {
    return await this.__getRequest(`/secure/message/${messageUUID}`, "GET");
  }
  async getPatientAccountStatus(email) {
    return await this.__getRequest(`/secure/clinician/email/${email}`, "GET");
  }
  async getPatientDetailByEmail(email) {
    return await this.__getRequest(`/secure/patient/detail/email/${email}`, "GET");
  }

  async getPatientDetailsBySubId(patientSubId) {
    return await this.__getRequest(`/secure/patient/detailsBySubId/${encodeURIComponent(patientSubId)}`, "GET");
  }

  async getMessageCount() {
    return await this.__getRequest("/secure/message/new/count", "GET", "C");
  }

  async getMessages() {
    return await this.__getRequest("/secure/message", "GET", "C");
  }

  async getMessageSent() {
    return await this.__getRequest("/secure/message/sent", "GET", "C");
  }

  async getMessageSentOthers() {
    return await this.__getRequest("/secure/message/sent/otherClinicians", "GET", "C");
  }

  async getTestDataByKitId(kitId) {
    return await this.__getRequest("/secure/testResult/cdd/labResultsForm?kitId=" + kitId, "GET", "C");
  }

  async createTestResult(testPayload) {
    return await this.__postRequest("/secure/testResult/cdd/labResults", "POST", testPayload);
  }

  async getUnassignedLabRequests() {
    return await this.__getRequest("/secure/labRequest?queryType=UNASSIGNED", "GET");
  }

  async getUnassignedLabResults() {
    return await this.__getRequest("/secure/testResult?queryType=", "GET");
  }

  async overrideForManualClinicianReview(kitId) {
    return await this.__postRequest(`/secure/testResult/overrideForManualClinicianReview/${kitId}`, "POST", kitId);
  }

  async getUsersReport() {
    return await this.__getRequest("/secure/report/users", "GET");
  }

  async putApproveLabRequest(data) {
    return await this.__postRequest("/secure/labRequest/approve", "PUT", data);
  }

  async uploadCddTestResultPdf(kitId, data) {
    const formData = new FormData();
    formData.append("file", data);
    return await this.__uploadMultiPartFileRequest(`/secure/testResult/uploadResultFile/cdd/${kitId}`, "POST", formData, "multipart/form-data");
  }

  async putAssignLabRequest(data) {
    return await this.__postRequest("/secure/labRequest/assign", "PUT", data);
  }

  async putAssignLabResult(data) {
    return await this.__postRequest("/secure/testResult/assign", "PUT", data);
  }

  async putRejectLabRequest(data) {
    return await this.__postRequest("/secure/labRequest/reject", "PUT", data);
  }

  async putTestResultNotes(noteKitId, data) {
    return await this.__postRequest(`/secure/testResult/${noteKitId}/notes`, "POST", data);
  }

  async putTestResultNotesRelease(kitId) {
    return await this.__postRequest(`/secure/testResult/${kitId}/notes/release`, "PUT", { kitId: kitId });
  }

  async registerCovidKit(data) {
    return await this.__postRequest("/secure/kit/covid", "POST", data);
  }

  async postAppointment(data) {
    return await this.__postRequest("/secure/message/appointment", "POST", data);
  }
  async getAppointmentByDate(date) {
    if (!date || typeof date !== 'string') {
      throw new Error('Date parameter is required and must be a string');
    }
    const trimmedDate = date.trim();
    if (!trimmedDate) {
      throw new Error('Date cannot be empty');
    }
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(trimmedDate)) {
      throw new Error('Invalid date format. Expected YYYY-MM-DD');
    }
    const dateObj = new Date(trimmedDate);
    if (isNaN(dateObj.getTime())) {
      throw new Error('Invalid date value');
    }
    // Date format validation above ensures only safe characters (digits and hyphens)
    return await this.__doBookingGET(`/bookings?date=${trimmedDate}`);
  }
  async getTestResultWithStatus(status) {
    return await this.__getRequest(`/secure/testResult/status/${status}`, "GET");
  }
  async getKitByStatus(status) {
    return await this.__getRequest(`/secure/kit/status/${status}`, "GET");
  }
  async getOrderByStatus(status) {
    return await this.__getRequest(`/default/order/status/${status}`, "GET");
  }

  async postLicense(data) {
    return await this.__postRequest("/secure/license", "POST", data);
  }

  async postMessageReply(data) {
    return await this.__postRequest("/secure/message/replyToPatient", "POST", data);
  }
  async getTestCompendium(labName, careLocation) {
    return await this.__getRequest(`/secure/kit/testCompendium?labName=${labName}&location=${careLocation}`, "GET");
  }
  async postCreateLabRequest(data) {
    return await this.__postRequest("/secure/kit/labOrderRequest", "POST", data);
  }

  async cancelLabOrders(data) {
    return await this.__postRequest("/secure/kit/cancelLabOrders", "PATCH", data);
  }

  async approveLabOrderSamples(data) {
    return await this.__postRequest("/secure/kit/approveLabOrderSamples", "PATCH", data);
  }

  async updateLabOrderWithShippingManifest(data) {
    return await this.__postRequest("/secure/kit/updateLabOrderWithShippingManifest", "PATCH", data);
  }

  async confirmLabOrderShippingManifest(data) {
    return await this.__postRequest("/secure/kit/confirmLabOrderShippingManifest", "PATCH", data);
  }

  /*---------------------
    Send new message API
  -----------------------*/

  //----Post new message to backend

  async postPatientMessage(data) {
    return await this.__postRequest("/secure/message/patient", "POST", data);
  }

  async uploadFile(data, fileUseCaseType) {
    const formData = new FormData();
    formData.append("file", data);
    return await this.__uploadMultiPartFileRequest(`/secure/file/upload/${fileUseCaseType}`, "POST", formData, "multipart/form-data");
  }

  async downloadFile(fileUseCaseType, fileName) {
    return await this.__getRequestWithResponseType(`/secure/file/download/${fileUseCaseType}/${fileName}`, "GET", "C", "blob");
  }

  async deleteFile(fileUseCaseType, fileName) {
    return await this.__getRequest(`/secure/file/delete/${fileUseCaseType}/${fileName}`, "DELETE");
  }

  async sendSecureMsgToPatient(data) {
    return await this.__postRequest("/secure/message/v2/toPatient", "POST", data);
  }

  async addAnnotationToMessage(data) {
    return await this.__postRequest(`/secure/message/addAnnotationToMsgs`, "POST", data);
  }
  //----Post the TO search Value

  async searchPatient(criteria) {
    return await this.__postRequest("/secure/patient/search", "POST", criteria);
  }

  //----Post the TO search Value

  async getAllPatients(criteria) {
    return await this.__postRequest("/secure/patient/list", "GET", criteria);
  }

  /*---------------------------
    Send new message API ends
  ----------------------------*/

  async postTestResult(data) {
    return await this.__postRequest("/secure/testResult", "POST", data);
  }

  async __doBookingGET(endpoint) {
    return this.__getRequest(bookingBaseUrlFor(endpoint), "GET");
  }

  async __doBookingPOST(endpoint, data) {
    return this.__postRequest(bookingBaseUrlFor(endpoint), "POST", data);
  }

  // Prep Lead Analytics APIs (Super Admin)
  async getPrepAnalyticsSummary() {
    return this.__getRequest('/secure/prep/analytics/summary', 'GET');
  }

  async getPrepAnalyticsLeads(params = {}) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.ingressType) qs.set('ingressType', params.ingressType);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.search) qs.set('search', params.search);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.lastEvaluatedKey) qs.set('lastEvaluatedKey', params.lastEvaluatedKey);
    const q = qs.toString();
    return this.__getRequest(`/secure/prep/analytics/leads${q ? `?${q}` : ''}`, 'GET');
  }

  async getPrepAnalyticsLeadById(leadId) {
    return this.__getRequest(`/secure/prep/analytics/leads/${encodeURIComponent(leadId)}`, 'GET');
  }

  async resendPrepLeadLink(leadId) {
    return this.__postRequest(`/secure/prep/analytics/leads/${encodeURIComponent(leadId)}/resend-link`, 'POST', {});
  }

  async exportPrepAnalyticsLeads(params = {}) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.ingressType) qs.set('ingressType', params.ingressType);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.search) qs.set('search', params.search);
    if (params.fields) qs.set('fields', Array.isArray(params.fields) ? params.fields.join(',') : params.fields);
    const q = qs.toString();
    return this.__getRequestWithResponseType(
      `/secure/prep/analytics/leads/export${q ? `?${q}` : ''}`, 'GET', 'C', 'blob'
    );
  }

  // APIs for Super Admin Panel 
  async createSaleorOrder(data) {
    return this.__postRequest(`/secure/clinician/saleorOrder`, "POST", data)
  }

  /** Remove patient from PrEP Maintenance. Backend: DELETE /secure/clinician/remove/prep-maintenance/{userId}, 202 ACCEPTED. */
  async removePrepMaintenance(userId) {
    return this.__postRequest(`/secure/clinician/remove/prep-maintenance/${encodeURIComponent(userId)}`, "DELETE", {});
  }

  async enrollPrepMaintenance(userId, data = {}) {
    return this.__postRequest(`/secure/enroll/prep-maintenance/${encodeURIComponent(userId)}`, "POST", { userId, ...data });
  }

  /**
   * Placeholder for PrEP download bundle (Jotform submission + attachments, Rx PDFs).
   * TODO: Replace with real GET when backend is ready. Expected: { jotformFiles: [{ filename, content }], rxPdfs: [{ filename, content }] }.
   */
  async getPatientPrepDownloadBundle(patientId) {
    return Promise.resolve({ data: { jotformFiles: [], rxPdfs: [] } });
  }

  // Internal API: PUBLIC_DOCUMENT; X-Internal-Api-Key (raw key) sent per SimpleApiKeyRequestFilter.
  async uploadDocument(file, bookingId, documentType) {
    if (!file) {
      throw new Error('File parameter is required');
    }
    if (!bookingId || typeof bookingId !== 'string') {
      throw new Error('BookingId parameter is required and must be a string');
    }
    if (!documentType || typeof documentType !== 'string') {
      throw new Error('DocumentType parameter is required and must be a string');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('bookingId', bookingId.trim());
    formData.append('documentType', documentType.trim());
    formData.append('fileUseCaseType', 'BOOKING_SELF_CHECK_IN');

    const path = INTERNAL_PATHS.PUBLIC_DOCUMENT;
    const rawJwt = await this.__getAuthorizationToken();
    const headers = {
      'Content-Type': 'multipart/form-data',
      ...getInternalApiKeyHeaders(),
    };
    if (rawJwt) {
      headers.authorization = `Bearer ${rawJwt}`;
    }

    return axios({
      method: 'POST',
      url: getBackendUrlFor(path),
      headers,
      data: formData,
    });
  }

  async __getAuthorizationToken() {
    // IMPORTANT: Use Access Token for API auth (ID token is UI-only)
    return await getAccessToken();
  }

  // Internal APIs (7 path groups): X-Internal-Api-Key with raw key (REACT_APP_INTERNAL_API_KEY). No HMAC.
  // All other APIs: JWT only; no x-api-key.
  async __getRequest(url, method, addresseeType = "C") {
    const rawJwt = await this.__getAuthorizationToken();
    const headers = {
      addresseeType: addresseeType,
      "Content-Type": "application/json",
    };
    if (rawJwt) {
      headers.authorization = `Bearer ${rawJwt}`;
    }
    if (isInternalPath(url)) {
      Object.assign(headers, getInternalApiKeyHeaders());
    }
    return axios({ method, url: getBackendUrlFor(url), headers });
  }

  async __getRequestWithResponseType(url, method, addresseeType = "C", responseType) {
    const rawJwt = await this.__getAuthorizationToken();
    const headers = {
      addresseeType: addresseeType,
      "Content-Type": "application/json",
    };
    if (rawJwt) {
      headers.authorization = `Bearer ${rawJwt}`;
    }
    if (isInternalPath(url)) {
      Object.assign(headers, getInternalApiKeyHeaders());
    }
    return axios({ method, url: getBackendUrlFor(url), responseType, headers });
  }

  async __postRequest(url, method, data, contentType = "application/json") {
    const rawJwt = await this.__getAuthorizationToken();
    const headers = {
      addresseeType: "C",
      "Content-Type": contentType,
    };
    if (rawJwt) {
      headers.authorization = `Bearer ${rawJwt}`;
    }
    if (isInternalPath(url)) {
      Object.assign(headers, getInternalApiKeyHeaders());
    }
    return axios({ method, url: getBackendUrlFor(url), headers, data });
  }

  async __uploadMultiPartFileRequest(url, method, data, contentType) {
    const rawJwt = await this.__getAuthorizationToken();
    const headers = {
      addresseeType: "C",
      "Content-Type": contentType,
    };
    if (rawJwt) {
      headers.authorization = `Bearer ${rawJwt}`;
    }
    if (isInternalPath(url)) {
      Object.assign(headers, getInternalApiKeyHeaders());
    }
    return axios({ method, url: getBackendUrlFor(url), headers, data });
  }
}

const getBackendBaseUrl = () => {
  return process.env.REACT_APP_SERVER_URL;
};

export const getCalcomBaseUrl = () => {
  return "https://booking.tbd.health/";
};

export const getMainSiteBaseUrl = () => {
  // return "http://localhost:3000";
  return process.env.REACT_APP_MAIN_STORE_SITE_URL;
};

const bookingBaseUrlFor = (endpoint) => {
  const bookingBaseUrl = BOOKING_BASE_PATH;
  return endpoint?.startsWith("/") ? `${bookingBaseUrl}${endpoint}` : `${bookingBaseUrl}/${endpoint}`;
};

const getBackendUrlFor = (endpoint) => {
  return endpoint?.startsWith("/") || getBackendBaseUrl().endsWith("/") ? `${getBackendBaseUrl()}${endpoint}` : `${getBackendBaseUrl()}/${endpoint}`;
};

const apiServiceInstance = new InternalApiService();
export default apiServiceInstance;
